import type { Material } from "three";
import type { SugarModelConfigurator } from "../../../loader/SugarModelConfigurator";
import {
  ObjectMaterialPort,
  type MaterialApplyResult,
  type ModelMaterialPort,
  type ModelMaterialSlot,
} from "../ModelMaterialPort";

/** `SugarModelConfigurator.applyMaterial` config map shape. */
export type ConfiguratorMaterialConfig = Map<
  string,
  { code: string; material: any }
>;

/**
 * Material port for `SugarModelConfigurator` bodies.
 *
 * The configurator applies materials as a whole config map (group key →
 * `{ code, material }`) rather than one code at a time, and the map itself is
 * owned by `SugarConfiguratorController`. So this port exposes `applyConfig`
 * for the bulk path and implements `applyByCode` on top of it by patching a
 * single entry — giving the configurator the same code-driven surface the
 * viewer already has.
 */
export class ConfiguratorMaterialPort implements ModelMaterialPort {
  private readonly objectPort: ObjectMaterialPort;
  private lastConfig: ConfiguratorMaterialConfig = new Map();

  constructor(private readonly configurator: SugarModelConfigurator) {
    this.objectPort = new ObjectMaterialPort(configurator);
  }

  list(): readonly ModelMaterialSlot[] {
    return this.objectPort.list();
  }

  apply(slotId: string, material: Material): void {
    this.objectPort.apply(slotId, material);
  }

  /** Bulk path — mirrors `SugarModelConfigurator.applyMaterial`. */
  async applyConfig(config: ConfiguratorMaterialConfig): Promise<unknown> {
    this.lastConfig = new Map(config);
    return this.configurator.applyMaterial(new Map(config));
  }

  async applyByCode(
    rawGroupCode: string,
    rawMaterialCode: string,
  ): Promise<MaterialApplyResult> {
    const groupCode = String(rawGroupCode ?? "").trim();
    const materialCode = String(rawMaterialCode ?? "").trim();

    if (!groupCode || !materialCode) {
      return {
        ok: false,
        groupCode,
        materialCode,
        error: "missing groupCode or materialCode",
      };
    }

    const existing = this.lastConfig.get(groupCode);
    const next: ConfiguratorMaterialConfig = new Map(this.lastConfig);
    next.set(groupCode, {
      ...(existing ?? { material: undefined }),
      code: materialCode,
    });

    try {
      await this.applyConfig(next);
      return { ok: true, groupCode, materialCode };
    } catch (err) {
      return {
        ok: false,
        groupCode,
        materialCode,
        error: err instanceof Error ? err.message : "apply failed",
      };
    }
  }
}
